import { FC, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIntl, defineMessages } from 'dibs-react-intl';
import SV from 'server-vars';
import classnames from 'classnames';

import PinterestIcon from 'dibs-icons/exports/legacy/Pinterest';
import { Link } from 'dibs-elements/exports/Link';

// helpers
import { trackPinterestClick } from '../../tracking/iconBubblesTracking';
import { pin } from 'dibs-social-sharing/exports/shareActions';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';

import { SocialShareVariant } from './types';
import { PdpSharedSocialSharePinterest_item$key } from './__generated__/PdpSharedSocialSharePinterest_item.graphql';

// styles
import styles from './PdpSharedSocialShareStyles';

// messages
const messages = defineMessages({
    label: {
        id: 'PdpSharedSocialSharePinterest.label',
        defaultMessage: 'Share {title} on pinterest',
    },
    description: {
        id: 'PdpSharedSocialSharePinterest.description',
        defaultMessage:
            '{title} | From a unique collection of {vertical, select, furniture {antique and modern} jewelry {vintage} fashion {rare vintage} other {} } {categoryName} at {categoryUrl}.',
    },
});

const itemFragment = graphql`
    fragment PdpSharedSocialSharePinterest_item on Item {
        title
        pdpURL
        vertical
        classification {
            categories {
                precedence
                category {
                    name
                    browseUrl
                }
            }
        }
        photos {
            largePath
        }
    }
`;

export const PdpSharedSocialSharePinterest: FC<
    {
        item: PdpSharedSocialSharePinterest_item$key;
    } & SocialShareVariant
> = ({ item: itemRef, variant = 'iconBubble', label }) => {
    const item = useFragment(itemFragment, itemRef);
    const intl = useIntl();

    const onClick = useCallback(() => {
        const categories = item?.classification?.categories || [];
        const { category } = categories.find(cat => cat?.precedence === 1) || {};

        // fire tracking
        trackPinterestClick(item.title);
        // launch pinterest window
        pin({
            newBrowserWindow: true,
            media: item?.photos?.[0]?.largePath || '',
            url: handleLocaleUrl(`${SV.get('localeOrigin')}${item.pdpURL}`, intl.locale, {
                intlLinkingMode: 'absolute',
            }),
            description: intl.formatMessage(messages.description, {
                title: item.title,
                vertical: item.vertical,
                categoryName: category?.name,
                categoryUrl: handleLocaleUrl(
                    `${SV.get('localeOrigin')}${category?.browseUrl}`,
                    intl.locale,
                    { intlLinkingMode: 'absolute' }
                ),
            }),
        });
    }, [item, intl]);

    const className = classnames(styles[`${variant}Variant`], styles.pinterest);

    return (
        <Link
            ariaLabel={intl.formatMessage(messages.label, { title: item.title })}
            className={className}
            onClick={onClick}
            role="menuitem"
            dataTn="pdp-shared-icon-pinterest"
        >
            <PinterestIcon className={styles.icon} />
            {label && <span>{label}</span>}
        </Link>
    );
};
